import { useState } from 'react';
import {
  Box, TextField, Select, MenuItem, FormControl,
  Typography, CircularProgress, Chip, Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSupport } from '../../hooks/useSupport';
import type { CreateTicketPayload, SupportTicketPriority } from '../../types/support';

const CATEGORIES = [
  { value: 'BugIssue',            label: 'Bug / Issue',            icon: '🐛' },
  { value: 'BillingSubscription', label: 'Billing & Subscription', icon: '💳' },
  { value: 'FeatureRequest',      label: 'Feature Request',         icon: '✨' },
  { value: 'GeneralSupport',      label: 'General Support',         icon: '💬' },
];

const PRIORITIES: { value: SupportTicketPriority; label: string; color: string; dot: string; sla: string }[] = [
  { value: 'Low',      label: 'Low',      color: '#6b7280', dot: '#9ca3af', sla: '48h' },
  { value: 'Normal',   label: 'Normal',   color: '#8A70D6', dot: '#8A70D6', sla: '24h' },
  { value: 'High',     label: 'High',     color: '#d97706', dot: '#f97316', sla: '8h'  },
  { value: 'Critical', label: 'Critical', color: '#dc2626', dot: '#ef4444', sla: '4h'  },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f9fafb', borderRadius: '8px', fontSize: '0.8rem',
    '& fieldset': { borderColor: '#e5e7eb' },
    '&:hover fieldset': { borderColor: '#d1d5db' },
    '&.Mui-focused fieldset': { borderColor: '#8A70D6', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#6b7280' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#8A70D6' },
  '& textarea::placeholder, & input::placeholder': { color: '#9ca3af', opacity: 1 },
  '& .MuiSelect-icon': { color: '#9ca3af' },
};

const menuSx = {
  '& .MuiPaper-root': {
    borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    border: '1px solid #f0f0f0', mt: 0.5,
  },
  '& .MuiMenuItem-root': {
    fontSize: '0.78rem', color: '#374151', py: 0.875,
    '&:hover': { bgcolor: '#f5f3ff' },
    '&.Mui-selected': { bgcolor: '#ede9fe', color: '#8A70D6', '&:hover': { bgcolor: '#ddd6fe' } },
  },
};

interface Props {
  onSuccess: () => void;
  currentModule?: string;
}

export function NewTicketForm({ onSuccess, currentModule }: Props) {
  const { useModules, useCreateTicket, useUploadAttachment } = useSupport();
  const { data: modules = [] } = useModules();
  const createMutation = useCreateTicket();
  const { files, isUploading, upload, remove, toJson } = useUploadAttachment();

  const [form, setForm] = useState<CreateTicketPayload>({
    subject: '',
    description: '',
    category: 'BugIssue',
    priority: 'Normal',
    module: currentModule ?? '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  const handleSubmit = () => {
    if (!form.subject.trim() || !form.description.trim()) return;
    createMutation.mutate(
      { ...form, attachmentsJson: toJson() },
      {
        onSuccess: () => {
          setSubmitted(true);
          setTimeout(() => { setSubmitted(false); onSuccess(); }, 1800);
        },
      },
    );
  };

  const selectedPriority = PRIORITIES.find((p) => p.value === form.priority)!;
  const isValid = form.subject.trim().length >= 3 && form.description.trim().length >= 10;

  if (submitted) {
    return (
      <Box sx={{
        textAlign: 'center', py: 6,
        animation: 'sFadeUp 0.3s ease',
        '@keyframes sFadeUp': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
      }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
          bgcolor: '#f0fdf4', border: '1.5px solid #bbf7d0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827', mb: 0.5 }}>
          Request Submitted!
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
          We'll respond within {selectedPriority.sla}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>

      {/* Subject */}
      <TextField
        label="Subject"
        size="small"
        fullWidth
        required
        value={form.subject}
        onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
        inputProps={{ maxLength: 200 }}
        sx={fieldSx}
      />

      {/* Category */}
      <Box>
        <Typography sx={{ fontSize: '0.67rem', fontWeight: 600, color: '#6b7280', mb: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Category
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
          {CATEGORIES.map((c) => (
            <Box
              key={c.value}
              onClick={() => setForm((f) => ({ ...f, category: c.value }))}
              sx={{
                p: 1.125, borderRadius: '9px', cursor: 'pointer',
                border: '1.5px solid', transition: 'all 0.15s',
                ...(form.category === c.value
                  ? { borderColor: '#8A70D6', bgcolor: '#f5f3ff' }
                  : { borderColor: '#e5e7eb', bgcolor: '#f9fafb', '&:hover': { borderColor: '#d1d5db', bgcolor: '#fff' } }
                ),
              }}
            >
              <Box sx={{ fontSize: '1.05rem', mb: 0.375, lineHeight: 1 }}>{c.icon}</Box>
              <Typography sx={{
                fontSize: '0.69rem', fontWeight: 600, lineHeight: 1.25,
                color: form.category === c.value ? '#8A70D6' : '#374151',
              }}>
                {c.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Priority */}
      <Box>
        <Typography sx={{ fontSize: '0.67rem', fontWeight: 600, color: '#6b7280', mb: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Priority
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.625 }}>
          {PRIORITIES.map((p) => (
            <Box
              key={p.value}
              onClick={() => setForm((f) => ({ ...f, priority: p.value }))}
              sx={{
                flex: 1, py: 0.875, px: 0.5, borderRadius: '8px', cursor: 'pointer',
                border: '1.5px solid', textAlign: 'center', transition: 'all 0.15s',
                ...(form.priority === p.value
                  ? { borderColor: p.dot, bgcolor: `${p.dot}12` }
                  : { borderColor: '#e5e7eb', bgcolor: '#f9fafb', '&:hover': { borderColor: '#d1d5db', bgcolor: '#fff' } }
                ),
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.dot, mx: 'auto', mb: 0.375 }} />
              <Typography sx={{ fontSize: '0.63rem', fontWeight: 700, color: form.priority === p.value ? p.color : '#6b7280', lineHeight: 1 }}>
                {p.label}
              </Typography>
              <Typography sx={{ fontSize: '0.57rem', color: form.priority === p.value ? p.dot : '#9ca3af', mt: 0.25 }}>
                {p.sla}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Module */}
      {modules.length > 0 && (
        <FormControl size="small" fullWidth sx={fieldSx}>
          <Select
            displayEmpty
            value={form.module ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))}
            renderValue={(v) =>
              v
                ? modules.find((m) => m.value === v)?.label ?? v
                : <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Module (optional)</span>
            }
            MenuProps={menuSx}
          >
            <MenuItem value=""><em style={{ fontStyle: 'normal', color: '#9ca3af' }}>— None —</em></MenuItem>
            {modules.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {/* Description */}
      <TextField
        label="Describe the issue"
        size="small"
        fullWidth
        required
        multiline
        rows={4}
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        placeholder="Steps to reproduce, what you expected vs what happened…"
        sx={fieldSx}
      />

      {/* Attachments */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <label htmlFor="new-ticket-file-input" style={{ cursor: 'pointer' }}>
            <input id="new-ticket-file-input" type="file" style={{ display: 'none' }} onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx,.xlsx,.csv,.txt" />
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.5, borderRadius: '7px', cursor: 'pointer',
              border: '1px dashed #d1d5db', bgcolor: '#f9fafb',
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#8A70D6', bgcolor: '#f5f3ff' },
            }}>
              {isUploading
                ? <CircularProgress size={11} sx={{ color: '#8A70D6' }} />
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
              }
              <Typography sx={{ fontSize: '0.68rem', color: '#6b7280' }}>
                {isUploading ? 'Uploading…' : 'Attach file'}
              </Typography>
            </Box>
          </label>
          {files.map((f) => (
            <Chip
              key={f.blobPath} label={f.name} size="small"
              onDelete={() => remove(f.blobPath)}
              sx={{
                height: 22, fontSize: '0.65rem',
                bgcolor: '#ede9fe', color: '#8A70D6',
                border: '1px solid #ddd6fe',
                '& .MuiChip-deleteIcon': { color: '#a78bfa', '&:hover': { color: '#8A70D6' } },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Submit */}
      <Button
        variant="contained"
        fullWidth
        disabled={!isValid || createMutation.isPending}
        onClick={handleSubmit}
        endIcon={createMutation.isPending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
        sx={{
          py: 1.125, borderRadius: '9px', fontWeight: 700, fontSize: '0.82rem',
          textTransform: 'none', letterSpacing: '0.01em',
          bgcolor: '#8A70D6', boxShadow: '0 2px 8px rgba(138,112,214,0.3)',
          '&:hover': { bgcolor: '#7a60c6', boxShadow: '0 4px 16px rgba(138,112,214,0.4)' },
          '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' },
        }}
      >
        {createMutation.isPending ? 'Submitting…' : 'Submit Request'}
      </Button>
    </Box>
  );
}
